/**
 * rerenderAfterCypAdhdAppointmentDateChange
 * ------------------------------------------
 * CYP ADHD counterpart to rerenderAfterAutismAppointmentDateChange.js.
 *
 * After a clinician / Support / Org Admin commits appointment-date changes on
 * the CYP ADHD Patient Overview (one dialog session may change several modules),
 * re-finalise every PDF that embeds those dates:
 *
 *   1. Stage CAR(s) -- for every affected stage whose section is already
 *      approved (a finalised PDF exists). Stages still in the "AI outputs
 *      ready for review" state are skipped: the date is only baked in at
 *      render time, and nothing has been rendered for them yet.
 *   2. Patient Report -- exactly once, via rerenderCypAdhdPatientReportIfStale,
 *      after all CAR work. Multiple module changes must never fan out into
 *      multiple concurrent PR renders.
 *
 * CYP ADHD is simpler than Adult Autism in one key way: each module key maps
 * to exactly one stage key (no DDI→History+SSI fan-out), and there are no
 * cross-stage date dependencies (DD does not print CI's date). This means
 * the translation from short appointment keys to stage keys is a 1:1 map.
 *
 * Module short keys vs stage keys:
 *   The Patient Overview and appointment-date storage work in SHORT keys --
 *   ci, ssi, dd, pad. The report pipeline works in STAGE keys --
 *   clinicalInterview, semiStructuredInterview, diagnosticDecision,
 *   postAssessmentDiscussion. Both maps are in cypAdhdStages.js.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getCypAdhdReportContentPathSegments } from '../constants/cypAdhdFirestorePaths';
import {
  CYP_ADHD_STAGE_KEYS,
  STAGE_TO_REPORT_PREFIX,
  STAGE_TO_PROGRESS_KEY,
} from '../constants/cypAdhdStages';
import { TriggerRenderCypAdhdDocTemplate } from './TriggerRenderCypAdhdDocTemplate';
import { rerenderCypAdhdPatientReportIfStale } from './autoCypAdhdPatientReport';
import { waitForStageRenderComplete } from './processingStatusHelpers';

// ── Key translation ─────────────────────────────────────────────────────────

/**
 * Short appointment key -> the stage key whose CAR prints that date.
 * CYP is a 1:1 relationship (unlike Autism where ddi fans out to 2 stages).
 *
 *   { ci: 'clinicalInterview', ssi: 'semiStructuredInterview',
 *     dd: 'diagnosticDecision', pad: 'postAssessmentDiscussion' }
 */
export const APPOINTMENT_KEY_TO_STAGE_KEY = Object.entries(STAGE_TO_PROGRESS_KEY)
  .reduce((acc, [stageKey, shortKey]) => {
    acc[shortKey] = stageKey;
    return acc;
  }, {});

/**
 * Inverse: stage key -> short appointment key.
 */
const STAGE_KEY_TO_APPOINTMENT_KEY = Object.entries(APPOINTMENT_KEY_TO_STAGE_KEY)
  .reduce((acc, [shortKey, stageKey]) => {
    acc[stageKey] = shortKey;
    return acc;
  }, {});

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Normalise callers that pass either a single moduleKey or a moduleKeys array.
 * Dedupes and drops unknown keys so a stray Autism key cannot reach the CYP
 * render path.
 */
function resolveModuleKeys(moduleKey, moduleKeys) {
  const raw = Array.isArray(moduleKeys)
    ? moduleKeys
    : (moduleKey ? [moduleKey] : []);
  const seen = new Set();
  const resolved = [];
  for (const key of raw) {
    if (!APPOINTMENT_KEY_TO_STAGE_KEY[key] || seen.has(key)) continue;
    seen.add(key);
    resolved.push(key);
  }
  return resolved;
}

/** Is this stage's section report approved (i.e. a finalised PDF exists)? */
const isStageApproved = (reportContent, stageKey) => {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  return reportContent?.[stageKey]?.[`${prefix}ReportApproved`] === true;
};

/**
 * Work out which stage CARs must be re-rendered for a set of changed short
 * keys, preserving canonical stage order. Only approved stages are included:
 * a stage still in "ready for review" has no PDF, so there is nothing to
 * re-render.
 *
 * @returns {string[]} stage keys in CYP_ADHD_STAGE_KEYS order
 */
function collectAffectedStageKeys(shortKeys, reportContent) {
  const candidates = new Set(
    shortKeys.map((key) => APPOINTMENT_KEY_TO_STAGE_KEY[key]).filter(Boolean),
  );
  return CYP_ADHD_STAGE_KEYS.filter(
    (stageKey) => candidates.has(stageKey) && isStageApproved(reportContent, stageKey),
  );
}

// ── Prediction (for the dialog confirm screen) ──────────────────────────────

/**
 * Predict which report artefacts will actually be re-finalised after the given
 * appointment-date changes, using the SAME gates the background re-render
 * applies. The dialog uses this so its confirm/success copy and the
 * "Go to Documents Dashboard" CTA only appear when a re-finalise will happen.
 *
 * @param {Object} params
 * @param {Object} [params.reportContent] stageKey -> reportContent doc data
 *                                        (from ModularPatientDataContext)
 * @param {string[]} [params.moduleKeys]  short keys being changed (ci/ssi/dd/pad)
 * @param {Object} [params.patientReportDoc] reportContent/patientReport doc data
 * @returns {{
 *   carsToRefinalise: string[],          short keys (for dialog labels)
 *   stagesToRefinalise: string[],        stage keys that will actually be rendered
 *   willRefinalisePatientReport: boolean,
 *   willRefinaliseAny: boolean,
 * }}
 */
export function predictCypAdhdAppointmentDateReportRefinalisation({
  reportContent = {},
  moduleKeys = [],
  patientReportDoc = null,
} = {}) {
  const keys = resolveModuleKeys(undefined, moduleKeys);
  const stagesToRefinalise = collectAffectedStageKeys(keys, reportContent);

  // Report the affected CARs back in short-key form for dialog labels.
  // CYP is 1:1 (no fan-out), but we still dedupe for safety.
  const carsToRefinalise = [];
  for (const stageKey of stagesToRefinalise) {
    const shortKey = STAGE_KEY_TO_APPOINTMENT_KEY[stageKey];
    if (shortKey && !carsToRefinalise.includes(shortKey)) carsToRefinalise.push(shortKey);
  }

  // Patient Report: requires all four sections approved AND a prior anchor.
  // appointmentDatesLastChangedAt will be bumped after save, making the PR
  // stale — but only if it was already rendered once (has an anchor).
  const allApproved = CYP_ADHD_STAGE_KEYS.every((stageKey) =>
    isStageApproved(reportContent, stageKey),
  );
  const willRefinalisePatientReport =
    keys.length > 0 &&
    allApproved &&
    !!patientReportDoc?.patientReportReportApprovedAt;

  return {
    carsToRefinalise,
    stagesToRefinalise,
    willRefinalisePatientReport,
    willRefinaliseAny: stagesToRefinalise.length > 0 || willRefinalisePatientReport,
  };
}

// ── Firestore reads ─────────────────────────────────────────────────────────

/**
 * Read every stage's reportContent doc + the patientReport doc fresh from
 * Firestore. We deliberately do not trust the caller's in-memory context here:
 * an approval could have landed since the overview last loaded.
 */
async function readFreshState(patientId) {
  const stageRefs = CYP_ADHD_STAGE_KEYS.map((stageKey) =>
    doc(db, ...getCypAdhdReportContentPathSegments(patientId, stageKey)),
  );
  const prRef = doc(db, ...getCypAdhdReportContentPathSegments(patientId, 'patientReport'));

  const [prSnap, ...stageSnaps] = await Promise.all([
    getDoc(prRef),
    ...stageRefs.map((ref) => getDoc(ref)),
  ]);

  const reportContent = {};
  CYP_ADHD_STAGE_KEYS.forEach((stageKey, i) => {
    reportContent[stageKey] = stageSnaps[i].exists() ? stageSnaps[i].data() : null;
  });
  const patientReportDoc = prSnap.exists() ? prSnap.data() : null;
  return { reportContent, patientReportDoc };
}

// ── Main entry point ────────────────────────────────────────────────────────

/**
 * Re-finalise the CYP ADHD PDFs affected by appointment-date changes.
 *
 * Wait-then-retry contract: if a stage CAR is already rendering when we arrive
 * (a clinician approved it seconds ago), that in-flight render read the OLD
 * date. We wait for it to finish, then trigger once more so the new date is
 * captured. We never queue more than one retry per stage.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} [params.moduleKey]    single short key (one-module callers)
 * @param {string[]} [params.moduleKeys] preferred: all short keys changed this session
 * @param {string} [params.clinicianId]  defaults to auth.currentUser.uid
 * @returns {Promise<{ success: boolean, cars?: Object, pr?: Object, skipped?: string }>}
 *   `cars` is keyed by STAGE key (one entry per stage render attempted/skipped).
 */
export async function rerenderAfterCypAdhdAppointmentDateChange({
  patientId,
  moduleKey,
  moduleKeys,
  clinicianId,
}) {
  const uid = clinicianId || auth.currentUser?.uid;
  const keys = resolveModuleKeys(moduleKey, moduleKeys);

  if (!patientId || !uid || keys.length === 0) {
    return { success: false, skipped: 'missing-params' };
  }

  let reportContent = {};
  try {
    ({ reportContent } = await readFreshState(patientId));
  } catch (err) {
    console.error('rerenderAfterCypAdhdAppointmentDateChange: failed to read patient state', err);
  }

  const stagesToRender = collectAffectedStageKeys(keys, reportContent);

  const results = { success: true, cars: {} };

  // 12 min: comfortably longer than the render CF's 6 min client timeout, and
  // matching the Patient Report waiter in autoCypAdhdPatientReport.
  const WAIT_TIMEOUT_MS = 12 * 60 * 1000;

  // ── Stage CARs (sequential so we never hammer the render CF) ────────────
  for (const stageKey of stagesToRender) {
    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    try {
      if (reportContent?.[stageKey]?.[`${prefix}ReportProcessingStatus`] === 'processing') {
        console.log(`CYP ${stageKey} CAR already rendering — waiting before re-render`);
        await waitForStageRenderComplete(patientId, stageKey, WAIT_TIMEOUT_MS);
      }
      results.cars[stageKey] = await TriggerRenderCypAdhdDocTemplate({
        patientId,
        stageKey,
        clinicianId: uid,
      });
    } catch (err) {
      console.error(`rerenderAfterCypAdhdAppointmentDateChange: CAR re-render failed (${stageKey})`, err);
      results.cars[stageKey] = { success: false, error: err?.message || String(err) };
    }
  }

  // Record skipped stages for caller/test visibility.
  for (const shortKey of keys) {
    const stageKey = APPOINTMENT_KEY_TO_STAGE_KEY[shortKey];
    if (stageKey && !(stageKey in results.cars)) {
      results.cars[stageKey] = { success: true, skipped: true, reason: 'module-not-approved' };
    }
  }

  // ── Patient Report — once only, after all CAR work ──────────────────────
  // The helper reads fresh state itself and applies its own gates (all four
  // sections approved; appointmentDatesLastChangedAt newer than the anchor).
  // Even if all four CARs re-rendered above, exactly one PR job is kicked off.
  try {
    results.pr = await rerenderCypAdhdPatientReportIfStale({ patientId, clinicianId: uid });
  } catch (err) {
    console.error('rerenderAfterCypAdhdAppointmentDateChange: PR re-render failed', err);
    results.pr = { success: false, error: err?.message || String(err) };
  }

  return results;
}
